// Chart of accounts table body only. Columns respect user Customize settings.
// Row is not clickable: navigation happens only through the Action column
// split-button (text navigates to Account history register or runs a report;
// chevron opens the actions menu). This keeps clicks on the Name / Account
// type / Detail type / balance cells from triggering navigation.
// Checkbox column: per-row selection drives the floating bulk-action banner.
// Mobile card view: whole card is still tappable until a mobile action menu lands.

import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Account } from "../types";
import type { TableCustomization } from "../../../../components/common/tableCustomization";
import { densityPaddingClass, visibleOrderedColumns } from "../../../../components/common/tableCustomization";
import { COA_COL_WIDTHS } from "../constants";

interface AccountsTableProps {
  accounts: Account[];
  customization: TableCustomization;
  selectedIds: Set<string>;
  onToggleRow: (id: string) => void;
  onRowClick: (id: string) => void;
  onEdit: (id: string) => void;
  onRunReport: (id: string) => void;
  onReconcile: (id: string) => void;
  onCreateSubaccount: (id: string) => void;
  onMakeInactive: (id: string) => void;
}

function fmtBalance(amount: number | null | undefined, nf: TableCustomization["numberFormat"]): string {
  if (amount == null) return "-";
  const sign = nf.hideCurrencySymbol ? "" : "$";
  const abs = Math.abs(amount);
  const str = abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (amount < 0) {
    return nf.negativeStyle === "parentheses" ? `(${sign}${str})` : `-${sign}${str}`;
  }
  return `${sign}${str}`;
}

function isReportOnlyAccount(a: Account): boolean {
  if (a.type === "Income" || a.type === "Expense") return true;
  if (a.category === "Cost of goods sold") return true;
  if (a.type === "Equity" && /retained\s*earnings/i.test(a.name)) return true;
  return false;
}

const COL_ALIGN: Record<string, "left" | "right"> = {
  name: "left",
  type: "left",
  detailType: "left",
  novalaBalance: "right",
  bankBalance: "right",
};

export default function AccountsTable(props: AccountsTableProps) {
  if (props.accounts.length === 0) {
    return (
      <div className="border border-line rounded-lg py-12 text-center text-ink3 text-[13px]">
        No accounts match your filter.
      </div>
    );
  }

  const visibleCols = visibleOrderedColumns(props.customization);
  const padY = densityPaddingClass(props.customization.density);
  const nf = props.customization.numberFormat;

  const renderCell = (col: string, a: Account): React.ReactNode => {
    switch (col) {
      case "name":
        return (
          <span className="inline-flex items-center">
            {a.parentId && (
              <span className="inline-block w-6 text-ink4 text-[14px]" aria-hidden="true">&#8627;</span>
            )}
            <span className="font-semibold text-ink">{a.name}</span>
          </span>
        );
      case "type":
        return a.category ?? a.type;
      case "detailType":
        return a.detailType ?? "-";
      case "novalaBalance":
        return <span className="tabular-nums whitespace-nowrap">{fmtBalance(a.balance, nf)}</span>;
      case "bankBalance":
        return <span className="tabular-nums whitespace-nowrap">-</span>;
      default:
        return null;
    }
  };

  const colW = (key: string): React.CSSProperties => {
    const w = COA_COL_WIDTHS[key];
    return w == null ? {} : { width: w };
  };

  return (
    <>
      <div className="hidden md:block">
        <table className="w-full border-collapse" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={colW("checkbox")} />
            {visibleCols.map((v) => <col key={v.key} style={colW(v.key)} />)}
            <col style={colW("action")} />
          </colgroup>
          <tbody>
            {props.accounts.map((a) => (
              <AccountRow
                key={a.id}
                account={a}
                visibleCols={visibleCols}
                padY={padY}
                selected={props.selectedIds.has(a.id)}
                onToggleRow={props.onToggleRow}
                renderCell={renderCell}
                onEdit={props.onEdit}
                onRunReport={props.onRunReport}
                onReconcile={props.onReconcile}
                onCreateSubaccount={props.onCreateSubaccount}
                onMakeInactive={props.onMakeInactive}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden">
        {props.accounts.map((a) => (
          <div key={a.id} onClick={() => props.onRowClick(a.id)} className="py-3 border-b border-lineSoft cursor-pointer hover:bg-rowHover">
            <div className="flex justify-between items-baseline gap-3">
              <div className="text-[13px] font-semibold text-ink truncate">
                {a.parentId && <span className="text-ink4 mr-1.5" aria-hidden="true">&#8627;</span>}
                {a.name}
              </div>
              <div className="text-[13px] font-bold text-ink tabular-nums whitespace-nowrap">{fmtBalance(a.balance, nf)}</div>
            </div>
            <div className="text-[11px] text-ink4 mt-1 font-medium">
              {a.number} &middot; {a.category ?? a.type}{a.detailType ? ` · ${a.detailType}` : ""}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

interface AccountRowProps {
  account: Account;
  visibleCols: Array<{ key: string; order: number }>;
  padY: string;
  selected: boolean;
  onToggleRow: (id: string) => void;
  renderCell: (col: string, a: Account) => React.ReactNode;
  onEdit: (id: string) => void;
  onRunReport: (id: string) => void;
  onReconcile: (id: string) => void;
  onCreateSubaccount: (id: string) => void;
  onMakeInactive: (id: string) => void;
}

function AccountRow({ account, visibleCols, padY, selected, onToggleRow, renderCell, onEdit, onRunReport, onReconcile, onCreateSubaccount, onMakeInactive }: AccountRowProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const action = (fn: (id: string) => void) => () => {
    fn(account.id);
    setOpen(false);
  };

  const reportOnly = isReportOnlyAccount(account);
  const mainLabel = reportOnly ? "Run report" : "Account history";

  const handleMainClick = () => {
    if (reportOnly) {
      onRunReport(account.id);
    } else {
      navigate(`/accounting/chart-of-accounts/${account.id}/history`);
    }
  };

  const rowBg = selected ? "bg-rowHover" : "hover:bg-rowHover";

  return (
    <tr className={rowBg}>
      <td className={`${padY} px-3.5 border-b border-lineSoft`}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleRow(account.id)}
          className="w-4 h-4 cursor-pointer accent-teal-dark"
          aria-label={`Select ${account.name}`}
        />
      </td>
      {visibleCols.map((v) => {
        const col = v.key;
        const align = COL_ALIGN[col] ?? "left";
        return (
          <td key={col} className={`${padY} px-3.5 border-b border-lineSoft text-[12.5px] text-ink ${align === "right" ? "text-right" : ""}`}>
            {renderCell(col, account)}
          </td>
        );
      })}
      <td className={`${padY} px-3.5 border-b border-lineSoft text-right`}>
        <div ref={ref} className="relative inline-flex items-center">
          <button
            type="button"
            onClick={handleMainClick}
            className="text-[11.5px] text-teal-dark font-bold cursor-pointer bg-transparent border-none px-1.5 py-1 hover:underline whitespace-nowrap"
          >
            {mainLabel}
          </button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-teal-dark hover:text-ink cursor-pointer bg-transparent border-none px-1 py-1 ml-0.5 rounded transition-colors hover:bg-panel inline-flex items-center"
            aria-label="More actions"
          >
            <ChevronDown size={12} strokeWidth={2.5} />
          </button>
          {open && (
            <div className="absolute right-0 top-full mt-1 w-[220px] bg-white border border-line rounded-lg shadow-[0_8px_24px_rgba(14,26,26,0.12)] py-1 z-40 overflow-hidden text-left">
              <RowDdItem label="Edit" onClick={action(onEdit)} />
              <RowDdItem label="Run report" onClick={action(onRunReport)} />
              <RowDdItem label="Reconcile" onClick={action(onReconcile)} />
              <RowDdItem label="Create subaccount" onClick={action(onCreateSubaccount)} />
              <div className="h-px bg-lineSoft my-1" />
              <RowDdItem label="Make inactive" onClick={action(onMakeInactive)} danger />
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

function RowDdItem({ label, onClick, danger }: { label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button type="button" onClick={onClick} className={`w-full text-left px-3 py-2 text-[12.5px] bg-transparent border-none cursor-pointer hover:bg-panel ${danger ? "text-red-600" : "text-ink"}`}>
      {label}
    </button>
  );
}